    import React, { useCallback, useContext, useEffect, useState } from "react";
import PageActionContext from "@stores/page-action-context";
import AppDataContext from "@stores/app-data-context";
import useForm, { buildInput } from "@ui/forms/use-form";
import FormHtmlInput from "@ui/forms/form-input";

const FormLoanRequirements = ({ data }) => {    
    const { vehiclePrice, deposit, baloonPayment, loanTerm, stockId, buildYear, assetCondition, minLoanAmount, maxLoanAmount, loanAmountRangeErrorMessage } = data;
    const [loanAmount, setLoanAmount] = useState(0);
    const [loanAmountIsValid, setLoanAmountIsValid] = useState(true);
    const pageActionCxt = useContext(PageActionContext);
    const appDataCxt = useContext(AppDataContext);
    const cxtData = appDataCxt.getLoanRequirements() ?? {};
    const trueVehiclePrice = (cxtData.stockId === stockId ? cxtData.vehiclePrice : vehiclePrice.value) || vehiclePrice.value; 
        
    const validateDepositAmount = ({ value, others }) => {
        const deposit = parseFloat(value, 10);
        const vehiclePrice = parseFloat(others.vehiclePrice, 10);

        validateLoanAmount(vehiclePrice - deposit);
        setLoanAmount(vehiclePrice - deposit);
        return (vehiclePrice - minLoanAmount >= deposit) 
    };
    
    const validateVehiclePrice = ({ value, others }) => {
        const deposit = parseFloat(others.deposit, 10);
        const loanValue = parseFloat(value, 10);

        if (deposit > 0) {
            validateLoanAmount(loanValue - deposit);
            setLoanAmount(loanValue - deposit);            
            return (deposit <= loanValue - minLoanAmount);
        }
        else {            
            validateLoanAmount(parseFloat(others.vehiclePrice, 10));
            setLoanAmount(parseFloat(others.vehiclePrice, 10));
            return true;
        }
    };

    const validateLoanAmount = (value) => {
        setLoanAmountIsValid((value >= minLoanAmount && value <= maxLoanAmount))        
        return loanAmountIsValid;
    };

    const inputs = [
        buildInput(vehiclePrice, trueVehiclePrice, [
            {
                name: "vehicle-minimum-amount-error",
                message: data.vehicleMinimumAmountError,
                check: useCallback((value) => validateVehiclePrice(value)),
            }
        ]),        
        buildInput(deposit, cxtData.deposit, [
            {
                name: "loan-deposit-error",
                message: data.loanDepositError,
                check: useCallback((value) => validateDepositAmount(value)),
            },
        ]),
        buildInput(baloonPayment, cxtData.baloonPayment),
        buildInput(loanTerm, cxtData.loanTerm),
    ];
           
    const form = useForm({
        inputs: inputs
    });

    useEffect(() => {
        pageActionCxt.reset();
    }, []);

    useEffect(() => {
        pageActionCxt.setNextDisabled(!form.isValid || !loanAmountIsValid);
    }, [form.isValid, loanAmountIsValid]);
       
    const handleSubmit = (e) => {
        e?.preventDefault();
        if (pageActionCxt.isNextDisabled) return;

        const loanRequirementsNewData = {
            vehiclePrice: parseInt(form.values.vehiclePrice, 10) ?? 0, 
            loanAmount: parseInt(loanAmount, 10) ?? 0,
            deposit: parseInt(form.values.deposit, 10) ?? 0,
            baloonPayment: parseInt(form.values.baloonPayment, 10) ?? 0,
            loanTerm: parseInt(form.values.loanTerm, 10) ?? 0,
            stockId: stockId,
            assetCondition: cxtData.assetCondition || assetCondition,
            buildYear: parseInt(cxtData.buildYear || buildYear, 10) ?? 0,
        };

        appDataCxt.setLoanRequirements(loanRequirementsNewData, form.dirty);

        const nextEventData = pageActionCxt.nextEventData;

        if (nextEventData) {
            pageActionCxt.navigate(nextEventData.url, nextEventData.isExternal);
        }
    };        
   
    useEffect(() => {
        if (pageActionCxt.isNextClicked) {
            handleSubmit(null);
            pageActionCxt.setNextClicked(false);
        }
    }, [pageActionCxt.isNextClicked]);

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <FormHtmlInput autoFocus={true} data={vehiclePrice} form={form}/>
            <FormHtmlInput data={deposit} form={form} />
            <div className="h-22">
                <div className="flex gap-4 h-22 bg-skin-primary-light rounded-md border border-skin-primary w-full px-4 py-4">
                    <div className="flex w-full text-center justify-center gap-2">
                    <p className="flex items-center text-lg">
                                {data.loanAmount.label}
                            </p>
                        <p className="flex items-center text-2xl text-skin-primary font-semibold">
                            {`${data.loanAmount.prefixSign}
                         ${ loanAmount > 0 ? loanAmount.toLocaleString() : 0 }`}</p>                
                    </div>                
                </div>
                {!loanAmountIsValid && <div class="text-red-600 text-sm">{loanAmountRangeErrorMessage}</div>}                
            </div>            
            <FormHtmlInput data={baloonPayment} form={form} />
            <FormHtmlInput data={loanTerm} form={form} />            
            <button type="submit" className="hidden">
                Submit
            </button>
        </form>
    );
};

export default FormLoanRequirements;
